/**
 * Permission codes the employee self-service screens declare an interest in.
 *
 * Every one of these is held by the seeded **Employee** role
 * (`core/rbac/roles.go`), and that is the whole point: ESS is the surface a
 * person with no other standing in the system has. If a screen in this module
 * needs a code the Employee role does not hold, the screen belongs in
 * `modules/hr` or `modules/payroll`, not here.
 *
 * Two of them are self-scoped **by name** rather than by the service layer —
 * `hr.employee.self.read` and `hr.employee.self.documents`. `internal/hr`'s own
 * comment on the Employee role explains why that distinction had to exist: a
 * permission that lets somebody read their own record must not be the same
 * signal that lets them read a colleague's, and the absence of that distinction
 * is exactly what once let any employee read any payslip.
 *
 * The other four are ordinary module codes that happen to be granted to
 * everybody, and are re-scoped server-side:
 *
 *  - `payroll.payslip.read` — the route is shared with the payroll officer's
 *    screens; `/me/payslips` resolves the employee from the JWT and 404s
 *    anybody else's payslip id.
 *  - `hr.leave.read`, `hr.attendance.read` — `selfEmployeeListScope` filters a
 *    holder without `hr.employee.read` down to their own rows.
 *  - `hr.leave.request` / `hr.leave.cancel` — `requireLeaveOwner` allows acting
 *    on one's own request and 404s anybody else's.
 *
 * `hr.leave_type.read` is here because the request form has to name a real
 * leave type; the Employee role holds it for that reason.
 */

export const ESS_PERMISSIONS = {
  /** Read one's own employee record. Never anybody else's. */
  selfRead: 'hr.employee.self.read',
  /** List and download the documents on one's own HR file. */
  selfDocuments: 'hr.employee.self.documents',

  payslipRead: 'payroll.payslip.read',

  leaveRead: 'hr.leave.read',
  leaveRequest: 'hr.leave.request',
  leaveCancel: 'hr.leave.cancel',
  leaveTypeRead: 'hr.leave_type.read',

  attendanceRead: 'hr.attendance.read',
} as const

export type EssPermission = (typeof ESS_PERMISSIONS)[keyof typeof ESS_PERMISSIONS]
